import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  effect,
  inject,
} from '@angular/core';
import { MobilePhoneAvailability } from '../../../models/mobilephone-search-enums';
import { FormGroup, FormControl, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { select } from '@ngxs/store';
import { IconNames } from '@app/shared/enums/icon-names';
import { CustomerSelectors, EquipmentUpgradeApprovedState } from '@app/sales/modules/details/states/customer.selectors';
import { PriceType } from '@app/shared/pipes/price-format.pipe';
import { DealerClaimType } from '@app/core/enums/dealer-claim-type';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { MatIcon } from '@angular/material/icon';
import { ExtendedCreditCheckComponent } from '@app/shared/components/extended-credit-check/extended-credit-check.component';
import { AllowSettingsDirective } from '@app/shared/directives/allow-settings.directive';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceSearchFieldComponent } from '@app/shared/components/molecules/ice-search-field/ice-search-field.component';

@Component({
  selector: 'app-mobilephone-search-filter',
  templateUrl: './mobilephone-search-filter.component.html',
  styleUrls: ['./mobilephone-search-filter.component.scss'],
  imports: [
    IceSearchFieldComponent,
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatCheckbox,
    AllowSettingsDirective,
    ExtendedCreditCheckComponent,
    MatIcon,
    IceButtonComponent,
    PriceFormatPipe,
  ],
})
export class MobilephoneSearchFilterComponent implements OnInit, OnChanges, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() isLoadingFilters: boolean;
  @Input() manufacturers: string[] = [];
  @Input() categories: string[] = [];
  @Input() selectedAvailableForList: MobilePhoneAvailability[] = [];
  @Input() selectedManufacturers: string[] = [];
  @Input() selectedCategories: string[] = [];
  @Input() searchText: string = '';
  @Input() isEquipmentUpgradeEnabled: boolean;
  @Input() isLoadingSearchResults: boolean = false;
  @Input() isManualRegistrationSupported: boolean;
  @Input() isMobilBytteBytte = false;

  @Output() registerManually = new EventEmitter();
  @Output() filterChange = new EventEmitter();

  PriceType = PriceType;
  IconNames = IconNames;
  EquipmentUpgradeApprovedState = EquipmentUpgradeApprovedState;
  DealerClaimType = DealerClaimType;

  subscriptions$: Subscription[] = [];

  canUseManualRegistration = select(DealerStateSelectors.canUseManualRegistration);
  hasPerformedAllowanceCheck = select(CustomerSelectors.hasPerformedAllowanceCheck);
  hasPerformedDetailedCreditCheck = select(CustomerSelectors.hasPerformedDetailedCreditCheck);
  equipmentUpgradeAllowance = select(CustomerSelectors.equipmentUpgradeAllowance);
  mobilBytteApproval = select(CustomerSelectors.mobilBytteApproval);
  isExistingCustomer = select(CustomerSelectors.isExistingCustomer);
  customerHasCreditCheckError = select(CustomerSelectors.hasCreditCheckError);

  constructor() {
    effect(() => {
      if (this.customerHasCreditCheckError()) {
        this.displayCreditCheckForm = true;
      } else if (!this.hasPerformedDetailedCreditCheck()) {
        this.displayCreditCheckForm = false;
      } else if (!this.customerHasCreditCheckError() && this.hasPerformedDetailedCreditCheck()) {
        this.displayCreditCheckForm = false;
      }

      this.changeDetectorRef.detectChanges();
    });
  }

  availableForList: MobilePhoneAvailability[] = [];
  searchForm: FormGroup;

  displayCreditCheckForm = false;

  get manufacturersFormList(): FormGroup[] {
    return (this.searchForm.controls.manufacturers as FormArray).controls as FormGroup[];
  }
  get availableForFormList(): FormGroup[] {
    return (this.searchForm.controls.availableFor as FormArray).controls as FormGroup[];
  }

  ngOnInit() {
    if (this.isEquipmentUpgradeEnabled) {
      this.availableForList.push(MobilePhoneAvailability.equipmentUpgrade);
    }

    this.searchForm = this.initForm();

    this.subscriptions$.push(
      this.searchForm.valueChanges.subscribe(value => {
        this.filterChange.emit(this.createFilterValues(value, this.searchText));
      })
    );
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.manufacturers || changes.categories) {
      this.searchForm = this.initForm();

      this.subscriptions$.push(
        this.searchForm.valueChanges.subscribe(value => {
          this.filterChange.emit(this.createFilterValues(value, this.searchText));
        })
      );
    }
  }

  handleCreditCheck() {
    this.displayCreditCheckForm = true;
  }

  createFilterValues(formValue, searchText: string) {
    return {
      searchText: searchText,
      manufacturers: formValue.manufacturers.filter(x => x.selected).map(y => y.name),
      availableFor: formValue.availableFor.filter(x => x.selected).map(y => y.name),
      categories: formValue.categories.filter(x => x.selected).map(y => y.name),
    };
  }

  private initForm(): FormGroup {
    return new FormGroup({
      manufacturers: new FormArray(
        this.manufacturers.map(
          manufacturer =>
            new FormGroup({
              name: new FormControl(manufacturer),
              selected: new FormControl(this.selectedManufacturers.includes(manufacturer)),
            })
        )
      ),
      availableFor: new FormArray(
        this.availableForList.map(
          availableFor =>
            new FormGroup({
              name: new FormControl(availableFor),
              selected: new FormControl(this.selectedAvailableForList.includes(availableFor)),
            })
        )
      ),
      categories: new FormArray(
        this.categories.map(
          category =>
            new FormGroup({
              name: new FormControl(category),
              selected: new FormControl(this.selectedCategories.includes(category)),
            })
        )
      ),
    });
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }
}
